import { BadgeList } from '@/components/badges'
import { useBadges } from '@/lib/verification'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Clock, MapPin, ShieldCheck, MessageCircle } from 'lucide-react'
import { BrandMark, BRAND_NAME } from '@/components/brand'
import { LanguageMenu, useT } from '@/i18n'
import { peso, fmtDate, phDay } from '@/lib/ph'
import { usePublicListing } from '@/lib/listing'
import { SkeletonRows, btnSecondary } from '@/components/rm'
import { useAuth } from '@/hooks/use-auth'
import { startAsClient } from '@/lib/messages'

// Spec 003. A trade person's public profile. Readable signed out. The data comes from the
// public_listing function, which returns public columns only: no phone, email or street address.
export const Route = createFileRoute('/p/$slug')({ component: PublicProfile })

function PublicProfile() {
  const { slug } = Route.useParams()
  const { t, lang } = useT()
  const q = usePublicListing(slug)
  const badges = useBadges([slug])
  const { session } = useAuth()
  const navigate = useNavigate()
  const p = q.data

  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-token-subtle bg-token-surface pt-[env(safe-area-inset-top,0px)]">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-4">
          <Link to="/" className="flex items-center gap-2"><BrandMark className="h-8 w-8" /><span className="hidden font-display text-[17px] font-bold text-token-primary min-[400px]:inline">{BRAND_NAME}</span></Link>
          <LanguageMenu className="ml-auto" />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-5">
        {q.isLoading && <SkeletonRows count={6} />}

        {!q.isLoading && !p && (
          <div className="rounded-[16px] border border-token-subtle bg-token-surface p-6 text-center">
            <h1 className="font-display text-[20px] font-bold text-token-primary">{t('pub.notFound')}</h1>
            <p className="mt-2 text-[14px] text-token-secondary">{t('pub.notFoundBody')}</p>
            <Link to="/" className="mt-4 inline-block text-[14px] font-semibold text-brand hover:underline">{t('pub.findOthers')}</Link>
          </div>
        )}

        {p && (
          <div className="space-y-5">
            <section className="rounded-[16px] border border-token-subtle bg-token-surface p-5">
              <div className="flex items-start gap-4">
                {p.logo
                  ? <img src={p.logo} alt="" className="h-16 w-16 flex-none rounded-[14px] border border-token-subtle bg-token-surface object-contain p-1" />
                  : <span className="flex h-16 w-16 flex-none items-center justify-center rounded-[14px] font-display text-[26px] font-bold text-brand-fg" style={{ background: p.accent_color || '#0038a8' }}>{p.business_name.slice(0, 1).toUpperCase()}</span>}
                <div className="min-w-0">
                  <h1 className="font-display text-[22px] font-bold leading-tight tracking-[-0.02em] text-token-primary">{p.business_name}</h1>
                  <p className="mt-1 text-[14px] text-token-secondary">{p.headline}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {p.categories.map((c) => (
                      <span key={c.slug} className="rounded-full bg-token-selected px-2.5 py-0.5 text-[12px] font-semibold text-brand">{lang === 'fil' ? c.name_fil : c.name_en}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-1 text-[13px] text-token-secondary">
                {p.years_experience != null && <p>{t('pub.years', { n: p.years_experience })}</p>}
                {p.member_since && <p>{t('pub.memberSince', { date: fmtDate(phDay(p.member_since), 'long') })}</p>}
              </div>
            </section>

            {badges.data?.get(p.slug) && (
              <section className="rounded-[16px] border border-token-subtle bg-token-surface p-5">
                <BadgeList b={badges.data.get(p.slug)} detail />
              </section>
            )}

            {p.about && (
              <section className="rounded-[16px] border border-token-subtle bg-token-surface p-5">
                <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.06em] text-token-muted">{t('pub.about')}</h2>
                <p className="whitespace-pre-line text-[14.5px] leading-relaxed text-token-primary">{p.about}</p>
              </section>
            )}

            <section className="rounded-[16px] border border-token-subtle bg-token-surface">
              <h2 className="px-5 pb-2 pt-5 text-[13px] font-bold uppercase tracking-[0.06em] text-token-muted">{t('pub.services')}</h2>
              <ul className="divide-y divide-token-subtle">
                {p.services.map((s) => (
                  <li key={s.id} className="flex items-start gap-3 px-5 py-3.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold text-token-primary">{s.name}</p>
                      {s.description && <p className="mt-0.5 text-[13px] text-token-secondary">{s.description}</p>}
                      <p className="mt-1 flex items-center gap-1 text-[12.5px] text-token-muted"><Clock className="h-3.5 w-3.5" />{t('pub.duration', { min: s.duration_min })}</p>
                    </div>
                    <div className="flex flex-none flex-col items-end gap-2">
                      <span className="text-[15px] font-bold tabular-nums text-token-primary">{s.price == null ? t('pub.askQuote') : peso(s.price)}</span>
                      {s.price != null
                        ? <Link to="/hire/$slug/$service" params={{ slug: p.slug, service: s.id }}
                            className="rounded-[10px] bg-brand px-3 py-1.5 text-[12.5px] font-semibold text-brand-fg hover:bg-brand-hover">{t('pub.book')}</Link>
                        : <Link to="/tasks/new" search={{ category: s.category_slug ?? undefined }}
                            className="rounded-[10px] border border-token-subtle px-3 py-1.5 text-[12.5px] font-semibold text-token-secondary">{t('pub.askQuote')}</Link>}
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-[16px] border border-token-subtle bg-token-surface p-5">
              <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.06em] text-token-muted">{t('pub.serves')}</h2>
              {p.base_city && (
                <p className="mb-2 flex items-center gap-1.5 text-[14px] text-token-primary"><MapPin className="h-4 w-4 text-brand" />
                  {t('pub.base', { place: p.base_city.name })}{p.radius_km ? ` ${t('pub.radius', { km: p.radius_km })}` : ''}
                </p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {p.area.map((a) => <span key={a.code} className="rounded-full border border-token-subtle px-2.5 py-0.5 text-[12.5px] text-token-secondary">{a.name}</span>)}
              </div>
            </section>

            <button type="button" className={`${btnSecondary} h-11 w-full justify-center`} onClick={async () => {
              if (!session) return navigate({ to: '/login' })
              const th = await startAsClient(p.slug)
              navigate({ to: '/messages/$id', params: { id: th } })
            }}><MessageCircle className="h-4 w-4" />{t('msg.ask')}</button>
            <p className="flex items-start gap-2 px-1 text-[12.5px] text-token-muted"><ShieldCheck className="mt-0.5 h-4 w-4 flex-none" />{t('pub.privacy')}</p>
          </div>
        )}
      </main>
    </div>
  )
}
